'use client'

import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { applyNavigationChange, navigationKeys, podeVerNavegacao } from '@/lib/navigation/navegacao'
import { emitCommand } from '@/lib/socket-command'
import { useAuth } from '@/providers/AuthContext'
import { useSocket } from '@/providers/SocketContext'
import { parseNavigationChanged } from '@/services/users/navigation'
import type { ChannelNavigation } from '@/types/navigation'

/**
 * Entra na sala de navegação (`enter_navigation`) e mantém o retrato
 * `['users','navigation']` vivo pelos `navigation_changed`. Devolve `joined`:
 * `true` só depois do ack `ok` NESTA conexão — é o que libera o GET
 * (`useNavigationSnapshot`), então o join vem antes do retrato por construção.
 *
 * Montado UMA vez, pelo `NavigationProvider` em `PrivateProviders`. Reentra pelo
 * effect com `socketReady` nas deps (e não por `on('connect')`, que sob essa guarda
 * é código morto na volta da queda).
 */
export function useNavigationLive(): boolean {
  const { user } = useAuth()
  const { socket, socketReady } = useSocket()
  const queryClient = useQueryClient()
  const pode = podeVerNavegacao(user)
  const [joined, setJoined] = useState(false)

  useEffect(() => {
    if (!socket || !socketReady || !pode) return

    let vivo = true

    const onChanged = (raw: unknown) => {
      const evt = parseNavigationChanged(raw)
      if (!evt) return
      const atual = queryClient.getQueryData<ChannelNavigation[]>(navigationKeys.snapshot)
      const proximo = applyNavigationChange(atual, evt)
      // Mesma referência = nada mudou (inclui retrato ainda não carregado). Sem
      // `setQueryData` aqui, nenhum consumidor re-renderiza à toa.
      if (proximo === atual) return
      // `setQueryData` grava `isInvalidated: false`: sem reafirmar, o evento que chega entre
      // o invalidate da reconexão e a query religar apagaria a marca de "velho" e o
      // retrato da volta da queda nunca seria rebuscado.
      const pendente = queryClient.getQueryState(navigationKeys.snapshot)?.isInvalidated
      queryClient.setQueryData<ChannelNavigation[]>(navigationKeys.snapshot, proximo)
      if (pendente) void queryClient.invalidateQueries({ queryKey: navigationKeys.snapshot, refetchType: 'none' })
    }
    // ANTES do emit: evento que chega logo depois do join não se perde.
    socket.on('navigation_changed', onChanged)

    void emitCommand(socket, 'enter_navigation').then((veredito) => {
      // `denied` (sem grant) e `unknown` (back sem o evento: sem ACK → timeout) são
      // ausência silenciosa — sem toast, sem retry.
      if (!vivo) return
      // Reingresso negado: o retrato de antes da queda não vale mais — ausência, não dado velho.
      if (veredito !== 'ok') {
        void queryClient.resetQueries({ queryKey: navigationKeys.snapshot, exact: true })
        return
      }
      // Na volta de uma queda a query está desligada (`joined` falso): o invalidate só
      // MARCA o retrato como velho, e religar a query com ele marcado refaz o GET. Sem
      // isso, o retrato de antes da queda ficaria até o staleTime, sem os eventos perdidos.
      void queryClient.invalidateQueries({ queryKey: navigationKeys.snapshot })
      setJoined(true)
    })

    return () => {
      vivo = false
      setJoined(false)
      socket.off('navigation_changed', onChanged)
      // Só com o socket de pé: na queda o sid já perdeu as salas, e o emit guardado pelo
      // socket.io-client sairia no sid NOVO, antes do `enter_navigation` da volta.
      if (socket.connected) socket.emit('leave_navigation')
    }
  }, [socket, socketReady, pode, queryClient])

  return joined
}
